<script setup lang="ts">
import { SelectOption } from '@/components/AppSelect.vue'
import { inquiryStatuses, inquiryTypes } from '@/constants'
import {
  INQUIRY_FILTER_OPTIONS,
  INQUIRY_ITEM,
  INQUIRY_STATUS_OPTION,
} from '@/types'
import { aiCoordinatorEndpoints, endpoints } from '@/utils/endpoints'
import {
  formatTime,
  formatTimeForRequest,
  generateTime,
  setDuration,
  singularize,
} from '@/utils/formatters'
import {
  matchInquiryStatusToCssVariable,
  matchInquiryStatusToIcon,
  matchInquiryTypeToIcon,
  matchInquiryTypeToShowType,
  matchInquiryTypeToStatusList,
} from '@/utils/matchers'
import {
  getInitialInquiryStatus,
  sendAddedInquiryEvent,
  sendChangedInquiryStatusEvent,
  sendEditedInquiryEvent,
} from '@/utils/mixpanel'
import { parseFormFields, sleep } from '@/utils/parsers'
import { inquirySchema } from '@/utils/schemas'
import { toTypedSchema } from '@vee-validate/zod'
import { addMinutes, roundToNearestMinutes, set } from 'date-fns'
import { TypedSchema, useForm, useValidateForm } from 'vee-validate'

const authStore = useAuthStore()
const inquiriesStore = useInquiriesStore()
const appStore = useAppStore()
const route = useRoute()

const props = defineProps({
  selectedInquiry: {
    type: Object as PropType<INQUIRY_ITEM>,
    required: false,
  },
  class: {
    type: String,
    required: false,
  },
})
const emits = defineEmits(['close', 'updated', 'ai-switched'])

const showInquiryTypePopUp = ref(false)
const showInquiryStatusPopUp = ref(false)
const errorText = ref('')
const isSending = ref(false)

const isReservationOrCateringOrPrivateParty = computed(() => {
  if (props.selectedInquiry?.is_other || props.selectedInquiry?.is_job) {
    return false
  }

  return true
})

const schema = !isReservationOrCateringOrPrivateParty.value
  ? inquirySchema.omit({
      start_time: true,
      end_time: true,
    })
  : inquirySchema

type ModifiedInquirySchema = Omit<TypedSchema, 'start_time' | 'end_time'>

const { handleSubmit, meta, setFieldValue } = useForm({
  validationSchema: toTypedSchema(schema) as ModifiedInquirySchema,
})

const inquiryTypesOptions: INQUIRY_FILTER_OPTIONS[] = inquiryTypes
  .filter((e) => e == 'Private Party' || e == 'Catering' || e == 'Reservation')
  .map((type) => ({
    inquiry_type: type,
    ...matchInquiryTypeToShowType(type),
    icon: matchInquiryTypeToIcon(type),
  }))

const statuses = props.selectedInquiry?.type
  ? matchInquiryTypeToStatusList(props.selectedInquiry.type)
  : inquiryStatuses

const inquiryStatusOptions: INQUIRY_STATUS_OPTION[] = statuses.map(
  (status) => ({
    label: status,
    variant: matchInquiryStatusToCssVariable(status),
    icon: matchInquiryStatusToIcon(status),
  }),
)

const selectedInquiryStatus = shallowRef(inquiryStatusOptions[0])

const selectedInquiryType = shallowRef<INQUIRY_FILTER_OPTIONS | null>()

if (!props.selectedInquiry?.id) {
  selectedInquiryStatus.value = inquiryStatusOptions.find(
    (status) => status.label === 'waiting on you',
  )!
} else {
  selectedInquiryStatus.value = inquiryStatusOptions.find(
    (status) => status.label === props.selectedInquiry?.status,
  )!

  selectedInquiryType.value = inquiryTypesOptions.find(
    (type) => type.inquiry_type === props.selectedInquiry?.type,
  )!
  setFieldValue('inquiryType', props.selectedInquiry?.type)
}

const today = ref(new Date())

const workingHoursStart = '00:00'
const workingHoursEnd = '24:00'
//const timeStep = appStore.matchSettingsTimeStep(props.selectedInquiry?.type)

const timeArray = ref<SelectOption[]>(
  generateTime(workingHoursStart, workingHoursEnd, 15).map(
    (time: Date, key: number) => ({
      id: key,
      text: formatTime(time),
      value: time,
    }),
  ),
)

type eventSpace = {
  id: number
  name: string
}

const spaceOptions = computed<SelectOption[]>(() => {
  if (appStore.spotInfo.event_spaces?.length) {
    return appStore.spotInfo.event_spaces.map((space: eventSpace) => ({
      id: space.id,
      text: space.name,
      value: space.name,
    }))
  }

  return []
})

// const cateringTypes = computed<SelectOption[]>(() => {
//   if (appStore.spotInfo.catering_settings.catering_types?.length) {
//     return appStore.spotInfo.catering_settings.catering_types.map(
//       (type: string, key: number) => ({
//         id: key,
//         text: type,
//         value: type,
//       }),
//     )
//   }

//   return []
// })

const initialBudget = computed(() => {
  if (!props.selectedInquiry) return ''

  if (props.selectedInquiry?.budget) return props.selectedInquiry?.budget

  if (props.selectedInquiry && props.selectedInquiry.form_fields) {
    const parsedFields = parseFormFields(props.selectedInquiry.form_fields)
    return parsedFields.budget ? parsedFields.budget : ''
  }
})

const showBudgetField = computed(() => {
  if (!props.selectedInquiry?.id) {
    return false
  }

  if (
    props.selectedInquiry?.budget &&
    Number(props.selectedInquiry?.budget) < 0
  ) {
    return false
  }

  if (props.selectedInquiry.budget) {
    return true
  }

  if (props.selectedInquiry && props.selectedInquiry.form_fields) {
    const parsedFields = parseFormFields(props.selectedInquiry.form_fields)

    return parsedFields.budget && parsedFields.budget > 0
  }
})

const initialDate = computed(() => {
  if (props.selectedInquiry?.year) {
    const date = set(new Date(), {
      year: props.selectedInquiry.year!,
      month: props.selectedInquiry.month! - 1,
      date: props.selectedInquiry.day!,
      hours: props.selectedInquiry.hour!,
      minutes: props.selectedInquiry.min!,
      seconds: 0,
    })

    setFieldValue('datepicker', date.toString())

    return date
  }

  return null
})

const startTime = computed(() => {
  if (
    props.selectedInquiry?.hour != null ||
    props.selectedInquiry?.min != null
  ) {
    let currentInqTime = set(new Date(), {
      year: props.selectedInquiry?.year!,
      month: props.selectedInquiry?.month! - 1,
      date: props.selectedInquiry?.day!,
      hours: props.selectedInquiry?.hour!,
      minutes: props.selectedInquiry?.min!,
      seconds: 0,
    })

    return timeArray.value.find((time) => {
      const timeValue = time.value as Date

      if (
        props.selectedInquiry?.hour === timeValue.getHours() &&
        calculateNearestMinutes(currentInqTime).getMinutes() ===
          timeValue.getMinutes()
      ) {
        setFieldValue('start_time', timeValue as Date)
        setFieldValue('end_time', timeValue as Date)

        return time
      }
    })
  }

  return {
    id: 0,
    text: '',
    value: '',
  }
})

const endTime = computed(() => {
  if (
    props.selectedInquiry?.hour != null ||
    props.selectedInquiry?.min != null
  ) {
    const endingInquiryTime = addMinutes(
      startTime.value!.value as Date,
      props.selectedInquiry.duration_min!,
    )

    return timeArray.value.find((time) => {
      const timeValue = time.value as Date

      if (
        calculateNearestMinutes(endingInquiryTime).getHours() ===
          timeValue.getHours() &&
        calculateNearestMinutes(endingInquiryTime).getMinutes() ===
          timeValue.getMinutes()
      ) {
        setFieldValue('start_time', timeValue as Date)
        setFieldValue('end_time', timeValue as Date)

        return time
      }
    })
  }

  return {
    id: 0,
    text: '',
    value: '',
  }
})

const initialDescription = computed(() => {
  if (props.selectedInquiry?.description) {
    setFieldValue('description', props.selectedInquiry?.description)
    return props.selectedInquiry?.description
  }

  return ''
})

const initialSpace = computed(() => {
  if (spaceOptions.value.length && props.selectedInquiry?.space_preference) {
    const space = spaceOptions.value.find(
      (space) => space.text === props.selectedInquiry?.space_preference,
    )

    setFieldValue('space', space?.text)
    return space
  }

  return {
    id: 0,
    text: '',
    value: '',
  }
})

const inquiry = ref({
  type: props.selectedInquiry?.type,
  name: props.selectedInquiry?.name ?? '',
  email: props.selectedInquiry?.email ?? '',
  phone: props.selectedInquiry?.phone ?? '',
  in_party: props.selectedInquiry?.in_party ?? '',
  description: initialDescription.value,
  start_time: startTime.value,
  end_time: endTime.value,
  date: initialDate.value,
  space: initialSpace.value,
  budget: initialBudget.value,
  status: selectedInquiryStatus.value.label,
  spot_id: authStore.spotId,
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  day: new Date().getDate(),
  hour: new Date().getHours() + 1,
  min: 0,
  duration_min: props.selectedInquiry?.duration_min ?? 0,
})

const handleSelectInquiryType = (option: INQUIRY_FILTER_OPTIONS) => {
  selectedInquiryType.value = option
  inquiry.value.type = selectedInquiryType.value?.inquiry_type
  setFieldValue('inquiryType', selectedInquiryType.value?.inquiry_type)
  showInquiryTypePopUp.value = false

  timeArray.value = generateTime(
    workingHoursStart,
    workingHoursEnd,
    appStore.matchSettingsTimeStep(selectedInquiryType.value?.inquiry_type),
  ).map((time: Date, key: number) => ({
    id: key,
    text: formatTime(time),
    value: time,
  }))
}

const handleSelectInquiryStatus = (option: INQUIRY_STATUS_OPTION) => {
  selectedInquiryStatus.value = option
  showInquiryStatusPopUp.value = false
  inquiry.value.status = option.label
}

const isFormDisabled = computed(() => !meta.value.valid || !meta.value.dirty)

const handleForm = handleSubmit(async () => {
  isSending.value = true

  const formattedDate = formatTimeForRequest(
    inquiry.value.date!,
    inquiry.value.start_time!.value as Date,
  )

  inquiry.value.year = formattedDate.year
  inquiry.value.month = formattedDate.month
  inquiry.value.day = formattedDate.day
  inquiry.value.hour = formattedDate.hour
  inquiry.value.min = formattedDate.min

  inquiry.value.duration_min = setDuration(
    inquiry.value.start_time!.value as Date,
    inquiry.value.end_time!.value as Date,
  )

  const { data } = await useApi(
    `${endpoints().createInquiry(authStore.spotId)}`,
  )
    .post(inquiry.value)
    .json()

  if (data.value && data.value.inquiries) {
    //here we should probably  apply filters from inquiry list page
    await inquiriesStore.fetchInquiries('All', 'Upcoming', 'Received')
    sendAddedInquiryEvent(data.value.inquiries[0], 'Preview')
    emits('close')
    return
  }

  await sleep(300)

  isSending.value = false
  errorText.value = 'Something went wrong. Please try again.'
})

async function fetchHandlingPreview(inquiryId: number) {
  const { data, error } = await useCoordinatorApi(
    aiCoordinatorEndpoints().inquiryHandlingPreview(),
  )
    .post({
      spot_id: authStore.spotId,
      inquiry_id: inquiryId,
      changes: {
        in_party: inquiry.value.in_party,
        year: inquiry.value.year,
        month: inquiry.value.month,
        day: inquiry.value.day,
        hour: inquiry.value.hour,
        min: inquiry.value.min,
        status: inquiry.value.status,
        type: inquiry.value.type,
      },
    })
    .json()

  return error.value
    ? null
    : (data.value as { show_popup?: boolean; reason?: 'tmt-settings' | 'ai-settings' | null } | null)
}

const handleUpdateInquiry = async () => {
  const oldStatus = getInitialInquiryStatus(props.selectedInquiry!)!

  isSending.value = true
  if (isReservationOrCateringOrPrivateParty.value) {
    const formattedDate = formatTimeForRequest(
      inquiry.value.date!,
      inquiry.value.start_time!.value as Date,
    )

    inquiry.value.year = formattedDate.year
    inquiry.value.month = formattedDate.month
    inquiry.value.day = formattedDate.day
    inquiry.value.hour = formattedDate.hour
    inquiry.value.min = formattedDate.min
    inquiry.value.duration_min = setDuration(
      inquiry.value.start_time!.value as Date,
      inquiry.value.end_time!.value as Date,
    )
  }

  const currentHandling = props.selectedInquiry!.ai_coordinator_handling
  const handlingPreview =
    appStore.aiCoordinatorActive &&
    props.selectedInquiry!.type === 'Private Party' &&
    ['response', 'forced'].includes(currentHandling)
      ? await fetchHandlingPreview(props.selectedInquiry!.id)
      : null
  const switchReason =
    handlingPreview?.reason === 'tmt-settings'
      ? 'party-settings'
      : handlingPreview?.reason === 'ai-settings' && currentHandling === 'response'
        ? 'ai-settings'
        : null

  const { data } = await useApi(
    `${endpoints().updateInquiry(authStore.spotId, props.selectedInquiry!.id)}`,
  )
    .post(inquiry.value)
    .json()
  const updated = data.value.inquiries[0]
  if (switchReason) {
    inquiriesStore.pendingManualHandling[updated.id] = Date.now() + 30_000
    updated.ai_coordinator_handling = 'off'
  }
  const index = inquiriesStore.inquiries.findIndex(
    (inquiry) => inquiry.id === props.selectedInquiry!.id,
  )

  inquiriesStore.inquiries[index] = data.value.inquiries[0]

  sendEditedInquiryEvent(data.value.inquiries[0])
  sendChangedInquiryStatusEvent(
    inquiriesStore.inquiries[index],
    oldStatus,
    route.name as string,
  )

  emits('updated', updated) 

  if (switchReason) {
    emits('ai-switched', switchReason)
  }
  emits('close')

  isSending.value = false
}

function calculateNearestMinutes(currentInqTime: Date) {
  return roundToNearestMinutes(currentInqTime, {
    nearestTo: 15,
  })
}
onMounted(async () => {
  const validate = useValidateForm()

  if (props.selectedInquiry?.id) {
    await validate()
  }
})
</script>
<template>
  <form
    :class="['flex w-full flex-col gap-3', props.class]"
    @submit.prevent="handleForm"
  >
    <div class="rounded-xl bg-white p-3.5">
      <div class="flex flex-col gap-2">
        <!-- status button -->
        <button
          v-if="selectedInquiryType"
          type="button"
          class="flex w-full items-center justify-between gap-1 rounded-full border border-brand-border p-3"
          :class="{ 'cursor-default': props.selectedInquiry?.id }"
          @click="
            () => {
              if (props.selectedInquiry?.id) return
              showInquiryTypePopUp = true
            }
          "
        >
          <div class="flex items-center justify-center gap-2">
            <Component :is="selectedInquiryType.icon" class="h-6 w-6" />
            <span>{{ singularize(selectedInquiryType!.label) }}</span>
          </div>
          <HeroiconsChevronDown
            v-if="!props.selectedInquiry?.id"
            class="h-5 w-5 text-black"
          />
        </button>
        <button
          v-else-if="!props.selectedInquiry?.id && !selectedInquiryType"
          type="button"
          class="flex w-full items-center justify-between gap-1 rounded-full border border-brand-border p-3"
          @click="showInquiryTypePopUp = true"
        >
          <div class="flex items-center justify-center gap-2">
            <span>Choose Inquiry Type</span>
          </div>
          <HeroiconsChevronDown class="h-5 w-5 text-black" />
        </button>
        <AppInput
          v-model="inquiry.name"
          class="!text-slate-800"
          variant="neutral"
          name="name"
          type="text"
          label="Name"
        ></AppInput>
        <AppInput
          v-if="props.selectedInquiry?.source != 'voice'"
          v-model="inquiry.email"
          class="!text-slate-800"
          variant="neutral"
          name="email"
          type="email"
          label="Email"
        ></AppInput>
        <AppInput
          v-model="inquiry.phone"
          class="!text-slate-800"
          variant="neutral"
          name="phone"
          label="Phone"
          type="tel"
          inputmode="numeric"
          placeholder="(___) ___-____"
        ></AppInput>

        <AppInput
          v-if="isReservationOrCateringOrPrivateParty"
          v-model="inquiry.in_party"
          class="!text-slate-800"
          variant="neutral"
          name="in_party"
          label="HeadCount"
          type="text"
        ></AppInput>
        <AppInput
          v-if="!props.selectedInquiry?.id"
          v-model="inquiry.description"
          class="!text-slate-800"
          variant="neutral"
          name="description"
          label="Description"
          type="text"
        ></AppInput>
        <div v-else>
          <label for="text-area" class="text-xs uppercase text-[#8B90A5]">
            Description
          </label>
          <textarea
            id="text-area"
            v-model="inquiry.description"
            name="description"
            label="Description"
            rows="4"
            class="w-full rounded-lg border-brand-neutral bg-brand-neutral px-2 focus:border-[#A8C8F8] focus:ring-[#A8C8F8]"
          ></textarea>
        </div>

        <template v-if="isReservationOrCateringOrPrivateParty">
          <div class="max-w-[11.5rem]">
            <AppDateTime
              placeholder="____, _ /_ /____"
              name="datepicker"
              :min-date="today"
              variant="date"
              :model-value="inquiry.date"
              @update:model-value="
                (value) => {
                  setFieldValue('datepicker', value?.toString())
                  inquiry.date = value as Date
                }
              "
            />
          </div>
            <label class="text-xs uppercase text-[#8B90A5]">
              Start / End Time
              <span class="text-red-500">*</span>
            </label>
          <div class="flex max-w-72 items-end justify-center gap-3">
            <AppSelect
              dropdown-height="max-h-[11em]"
              variant="neutral"
              name="start_time"
              placeholder="__ : __ AM"
              :model-value="inquiry.start_time"
              :options="timeArray"
              @update:model-value="
                (date) => {
                  inquiry.start_time = date
                  setFieldValue('start_time', date.value as Date)
                }
              "
            />
            <AppSelect
              class="text-slate-800"
              dropdown-height="max-h-[11em]"
              variant="neutral"
              name="end_time"
              placeholder="__ : __ PM"
              :model-value="inquiry.end_time"
              :options="timeArray"
              @update:model-value="
                (date) => {
                  inquiry.end_time = date
                  setFieldValue('end_time', date.value as Date)
                }
              "
            />
          </div>
          <div class="flex items-end gap-4">
            <AppSelect
              v-if="
                spaceOptions.length &&
                selectedInquiryType?.label == 'Private Party'
              "
              label="Space"
              class="min-w-56"
              variant="neutral"
              name="space"
              :model-value="inquiry.space"
              :options="spaceOptions"
              @update:model-value="
                (value) => {
                  setFieldValue('space', value.text)
                  inquiry.space = value
                }
              "
            />
            <AppInput
              v-if="showBudgetField"
              v-model="inquiry.budget"
              class="max-w-20 cursor-default select-none !text-slate-800 focus:border-transparent focus:outline-none focus:ring-0"
              variant="neutral"
              name="budget"
              label="Budget"
              type="text"
              :money-mask="true"
              readonly
            ></AppInput>
          </div>
        </template>
        <div class="flex flex-col text-left">
          <p class="ml-3 text-[#28282A]">Change Status</p>

          <AppButton
            type="button"
            class="mt-1 !bg-[var(--color)]"
            variant="secondary"
            size="md"
            :is-rounded="true"
            :is-wide="true"
            :style="{
              '--color': `hsl(var(--color-status-${selectedInquiryStatus!.variant}))`,
            }"
            @click="showInquiryStatusPopUp = true"
          >
            <Component
              :is="selectedInquiryStatus.icon"
              v-if="selectedInquiryStatus"
              class="h-6 w-6"
            />
            <span class="capitalize">
              {{ selectedInquiryStatus?.label }}
            </span>
          </AppButton>

          <AppButton
            v-if="!props.selectedInquiry?.id"
            class="mt-4"
            :variant="isFormDisabled ? 'secondary' : 'primary'"
            size="md"
            :is-rounded="true"
            :is-wide="true"
            :disabled="isFormDisabled || isSending"
          >
            <span class="capitalize">Create Inquiry</span>
          </AppButton>

          <p class="mt-3 text-brand-danger">{{ errorText }}</p>
        </div>
      </div>
    </div>
    <div class="min-w-xs mx-auto flex flex-col items-center">
      <AppButton
        v-if="props.selectedInquiry?.id"
        class="w-32"
        variant="primary"
        size="md"
        :is-rounded="true"
        :is-wide="true"
        :disabled="isFormDisabled || isSending"
        type="button"
        @click="handleUpdateInquiry"
      >
        <AppiconCheck class="h-4 w-4 fill-white" />
        <span class="capitalize">Save</span>
      </AppButton>
    </div>
  </form>
  <AppDialog
    :is-open="showInquiryTypePopUp"
    :can-close="true"
    :has-close-button="true"
    @close="showInquiryTypePopUp = false"
  >
    <template #title>
      <p class="font-semibold text-brand-black">Choose inquiry type</p>
    </template>
    <template #content>
      <AppList
        v-for="option in inquiryTypesOptions"
        :key="option.label"
        name="filter"
        :checked="option.label === selectedInquiryType?.label"
        :label="singularize(option.label)"
        :icon="option.icon"
        :show-radio-input="false"
        icon-classes="w-5"
        @click="handleSelectInquiryType(option)"
      />
    </template>
  </AppDialog>

  <AppDialog
    :is-open="showInquiryStatusPopUp"
    :can-close="true"
    :has-close-button="true"
    :teleport-to="
      appStore.spotHopperDesktopDetailsPage === true ? '#sidebar' : undefined
    "
    @close="showInquiryStatusPopUp = false"
  >
    <template #title>
      <p class="font-semibold text-gray-500">Set Status To</p>
    </template>
    <template #content>
      <AppList
        v-for="option in inquiryStatusOptions.filter(
          (option: INQUIRY_STATUS_OPTION) =>
            option.label !== 'autoresponse sent',
        )"
        :key="option.label as string"
        name="filter"
        :checked="option.label === selectedInquiryStatus?.label"
        :label="option.label as string"
        :icon="option.icon"
        :show-radio-input="false"
        icon-classes="fill-[var(--color-status)] w-6"
        :icon-variant="option.variant"
        class="capitalize"
        @click="handleSelectInquiryStatus(option)"
      />
    </template>
  </AppDialog>
</template>
